// ─────────────────────────────────────────────────────────────────────────────
// Diagnóstico del arranque de sesión. Offline access está habilitado desde
// agosto de 2026. Diferenciamos storage perdido, refresh token presente y edad
// conocida de la familia. Una edad desconocida nunca se infiere del canario,
// del access token ni de la fecha de la última rotación.
//
// El único momento en que se pueden distinguir es JUSTO ANTES de llamar a
// `getAccessTokenSilently()`: auth0-spa-js limpia su propio cache cuando el
// silent auth falla, así que después ya no se puede saber si estaba o no.
// Por eso el snapshot se toma antes y se adjunta al evento de la falla.
// ─────────────────────────────────────────────────────────────────────────────
import { snapshotRefreshToken, type RefreshTokenSnapshot } from '@/auth/refreshTokenDiagnostics';

const CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || '';

/** Prefijo del cache de auth0-spa-js en localStorage (cacheLocation="localstorage"). */
const AUTH0_CACHE_PREFIX = '@@auth0spajs@@';

// Claves que SOLO existen después de una sesión autenticada de verdad. Es lo que
// distingue "vendor que perdió la sesión" de "visitante que nunca tuvo una", y de
// eso depende que no inundemos PostHog con tráfico anónimo.
//   agora-setup-progress-owner → la estampa el ÉXITO del callback de Auth0.
//   analytics_last_identify_key → la escribe identifyUser, que necesita sesión.
// A propósito NO están acá:
//   redirectUri → la escribe el propio rebote a /login, así que un anónimo que
//     rebota una vez quedaría marcado como "tuvo sesión" para siempre.
//   onboarding_plan → sale de un ?plan= en la URL, antes de cualquier alta.
const OWN_LONG_LIVED_KEYS = ['agora-setup-progress-owner', 'analytics_last_identify_key'];

// Canario de storage: se planta una vez y jamás se reescribe. Su edad es la
// medición directa de cuánto vive el localStorage en este navegador. Hoy nace
// en 0 para todos, pero a partir de la semana que viene es el instrumento que
// separa el caso A del B sin depender de inferencias.
const STORAGE_CANARY_KEY = 'agora_storage_canary';

export interface AuthStorageSnapshot extends RefreshTokenSnapshot {
  /** El cache de auth0-spa-js seguía en localStorage al arrancar. */
  has_auth0_cache: boolean;
  /** La cookie `auth0.<clientId>.is.authenticated` (first-party, escrita por JS). */
  has_is_auth_cookie: boolean;
  /** Alguna clave nuestra de vida larga sobrevivió. */
  has_own_keys: boolean;
  /** Cantidad de claves en localStorage (0 = storage virgen o barrido). */
  ls_key_count: number;
  /** Edad del canario en días; -1 si no está (nunca plantado o borrado). */
  canary_age_days: number;
  /** false = localStorage inaccesible (modo restrictivo / private browsing). */
  storage_available: boolean;
  /**
   * Había evidencia de una sesión previa en este dispositivo. false = visitante
   * anónimo rebotando a /login, que es comportamiento normal y NO el bug.
   */
  had_prior_session: boolean;
}

const EMPTY_SNAPSHOT: AuthStorageSnapshot = {
  has_refresh_token: false,
  refresh_token_family_age_days: null,
  refresh_token_age_known: false,
  has_auth0_cache: false,
  has_is_auth_cookie: false,
  has_own_keys: false,
  ls_key_count: 0,
  canary_age_days: -1,
  storage_available: false,
  had_prior_session: false
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Foto del estado de storage/cookies ANTES de tocar Auth0. Nunca tira: si el
 * storage no está disponible devuelve el snapshot vacío. Es diagnóstico puro —
 * jamás debe poder romper el arranque de la app.
 */
export const snapshotAuthStorage = (): AuthStorageSnapshot => {
  try {
    let hasAuth0Cache = false;
    let hasOwnKeys = false;
    const total = localStorage.length;

    for (let i = 0; i < total; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith(`${AUTH0_CACHE_PREFIX}::${CLIENT_ID}::`)) hasAuth0Cache = true;
      if (OWN_LONG_LIVED_KEYS.includes(key)) hasOwnKeys = true;
    }

    const rawCanary = Number(localStorage.getItem(STORAGE_CANARY_KEY));
    const canaryAgeDays =
      Number.isFinite(rawCanary) && rawCanary > 0 ? Math.floor((Date.now() - rawCanary) / DAY_MS) : -1;

    // A mano, NO con readCookie(): ese helper hace JSON.parse del valor y devuelve
    // `[]` cuando la cookie no está. Para esta cookie el valor es `true`, así que
    // devolvía el BOOLEANO true y `.length` daba undefined → el chequeo era
    // siempre false. Además su regex no escapa el nombre (los puntos de
    // `auth0.<id>.is.authenticated` funcionarían como comodines) y el JSON.parse
    // puede tirar, lo que acá se comería el snapshot entero.
    const hasIsAuthCookie = CLIENT_ID
      ? document.cookie.split('; ').some((c) => c.startsWith(`auth0.${CLIENT_ID}.is.authenticated=`))
      : false;

    return {
      ...snapshotRefreshToken(CLIENT_ID, AUDIENCE),
      has_auth0_cache: hasAuth0Cache,
      has_is_auth_cookie: hasIsAuthCookie,
      has_own_keys: hasOwnKeys,
      ls_key_count: total,
      canary_age_days: canaryAgeDays,
      storage_available: true,
      // OJO: el canario NO entra acá. Se planta en CADA arranque, incluso en la
      // primera visita de un anónimo, así que su presencia no prueba que haya
      // existido una sesión — solo mide cuánto vive el localStorage.
      had_prior_session: hasAuth0Cache || hasIsAuthCookie || hasOwnKeys
    };
  } catch {
    return EMPTY_SNAPSHOT;
  }
};

// El snapshot TIENE que tomarse antes de que corra una sola línea de Auth0. El
// efecto del Router está gateado por `isLoading` de useAuth0(), que recién se
// apaga DESPUÉS de que <Auth0Provider> corrió su propio checkSession() — para
// entonces el SDK ya pudo haber limpiado su cache, y `has_auth0_cache` leería
// false hasta en el caso A. Por eso se captura en el boot (index.tsx, antes del
// render) y se congela acá.
let bootSnapshot: AuthStorageSnapshot | null = null;

/**
 * Congela la foto del storage del arranque. Se llama desde `src/index.tsx` antes
 * de montar React. Idempotente: la primera gana, las siguientes no la pisan.
 */
export const captureBootSnapshot = (): void => {
  if (bootSnapshot) return;
  bootSnapshot = snapshotAuthStorage();
  plantStorageCanary();
};

/**
 * La foto del arranque. Si por lo que sea nadie llamó a `captureBootSnapshot()`
 * (orden de imports, un entry alternativo), cae a tomarla en el momento: peor
 * dato que el congelado, pero nunca null.
 */
export const getBootSnapshot = (): AuthStorageSnapshot => {
  if (!bootSnapshot) captureBootSnapshot();
  return bootSnapshot as AuthStorageSnapshot;
};

/** Planta el canario si no está. Idempotente: si ya existe NO se pisa. */
export const plantStorageCanary = (): void => {
  try {
    if (!localStorage.getItem(STORAGE_CANARY_KEY)) {
      localStorage.setItem(STORAGE_CANARY_KEY, String(Date.now()));
    }
  } catch {
    // sin localStorage no hay canario que plantar
  }
};

export interface AuthBootstrapFailure {
  /** Código de Auth0: login_required, timeout, missing_refresh_token, consent_required… */
  error_type: string;
  /** Nombre de la clase de error: GenericError, TimeoutError, AuthenticationError… */
  error_name: string;
  error_message: string;
  /** navigator.onLine al momento de la falla — separa "sesión muerta" de "sin red". */
  online: boolean;
  /**
   * El iframe de silent auth puede expirar en una pestaña de fondo. NO se llama
   * `visibility`: esa clave ya está tipada como BOOLEANA en la taxonomía del
   * proyecto, así que un string no castea y PostHog lo lee null (verificado en
   * prod el 5/ago/2026 con los primeros eventos).
   */
  visibility_state: string;
}

/**
 * Normaliza el error de `getAccessTokenSilently()`. auth0-spa-js tira varias
 * formas distintas (GenericError con .error, TimeoutError, un Error pelado de
 * red), y el `catch` de Router las colapsaba TODAS en el mismo rebote a /login
 * sin siquiera bindear la variable. Esa era la razón por la que 18 meses de
 * este bug no dejaron una sola huella en PostHog.
 */
export const describeBootstrapFailure = (error: unknown): AuthBootstrapFailure => {
  const e = (error ?? {}) as Record<string, unknown>;
  const rawMessage = typeof e.message === 'string' ? e.message : '';

  return {
    error_type: typeof e.error === 'string' && e.error ? e.error : 'unknown',
    error_name: typeof e.name === 'string' && e.name ? e.name : 'unknown',
    // Capado: el mensaje es para clasificar, no para leer stack traces.
    error_message: rawMessage.slice(0, 160),
    online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
    visibility_state: typeof document !== 'undefined' ? document.visibilityState : 'unknown'
  };
};
